import {
  AssessmentStageFragmentFragment,
  LivekitAgentName,
  ProjectPageGetProjectByIdDocument,
  useUpdateAiInterviewAssessmentStageActionIsAvatarEnabledMutation,
  useUpdateAiInterviewAssessmentStageActionIsTimingAgentEnabledMutation,
  useUpdateAiInterviewAssessmentStageActionLivekitAgentNameMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import {
  Button,
  Modal,
  SelectFormField,
  Switch,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

type AiInterviewAction = NonNullable<
  AssessmentStageFragmentFragment['actions']['aiInterview']
>;

interface IEditAiInterviewActionModal {
  aiInterviewAction: AiInterviewAction;
  projectId: number;
  onClose: () => void;
}

const LIVEKIT_AGENT_OPTIONS: TSelectOption<LivekitAgentName>[] = [
  { label: 'AI Interviewer', value: LivekitAgentName.AiInterviewer },
  {
    label: 'AI Interviewer (Gemini)',
    value: LivekitAgentName.AiInterviewerGemini,
  },
  {
    label: 'AI Interviewer - Cascade',
    value: LivekitAgentName.AiInterviewerCascade,
  },
];

const EditAiInterviewActionModal: FC<IEditAiInterviewActionModal> = ({
  aiInterviewAction,
  projectId,
  onClose,
}) => {
  const { handleMsgType } = useNotification();
  const [submitting, setSubmitting] = useState(false);

  const initialAgent =
    aiInterviewAction.livekitAgentName ?? LivekitAgentName.AiInterviewer;

  const [livekitAgentName, setLivekitAgentName] =
    useState<LivekitAgentName>(initialAgent);
  const [isAvatarEnabled, setIsAvatarEnabled] = useState(
    aiInterviewAction.isAvatarEnabled
  );
  const [isTimingAgentEnabled, setIsTimingAgentEnabled] = useState(
    aiInterviewAction.isTimingAgentEnabled
  );

  const refetchQueries = [
    {
      query: ProjectPageGetProjectByIdDocument,
      variables: { id: projectId },
    },
  ];

  const [updateLivekitAgentName] =
    useUpdateAiInterviewAssessmentStageActionLivekitAgentNameMutation({
      refetchQueries,
    });

  const [updateIsAvatarEnabled] =
    useUpdateAiInterviewAssessmentStageActionIsAvatarEnabledMutation({
      refetchQueries,
    });

  const [updateIsTimingAgentEnabled] =
    useUpdateAiInterviewAssessmentStageActionIsTimingAgentEnabledMutation({
      refetchQueries,
    });

  const selectedOption =
    LIVEKIT_AGENT_OPTIONS.find(option => option.value === livekitAgentName) ??
    LIVEKIT_AGENT_OPTIONS[0];

  const handleAgentChange = (option: TSelectOption<LivekitAgentName>) => {
    if (option.value) {
      setLivekitAgentName(option.value);
    }
  };

  const hasChanges =
    livekitAgentName !== initialAgent ||
    isAvatarEnabled !== aiInterviewAction.isAvatarEnabled ||
    isTimingAgentEnabled !== aiInterviewAction.isTimingAgentEnabled;

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      if (livekitAgentName !== initialAgent) {
        await updateLivekitAgentName({
          variables: { id: aiInterviewAction.id, livekitAgentName },
        });
      }
      if (isAvatarEnabled !== aiInterviewAction.isAvatarEnabled) {
        await updateIsAvatarEnabled({
          variables: { id: aiInterviewAction.id, isAvatarEnabled },
        });
      }
      if (isTimingAgentEnabled !== aiInterviewAction.isTimingAgentEnabled) {
        await updateIsTimingAgentEnabled({
          variables: { id: aiInterviewAction.id, isTimingAgentEnabled },
        });
      }
      handleMsgType({
        type: TNotification.success,
        title: 'AI interview configuration updated',
      });
      onClose();
    } catch (_error) {
      handleMsgType({
        type: TNotification.error,
        title: 'Could not update AI interview configuration',
        message: 'Something went wrong. Please try again.',
      });
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      isOpen
      className="edit-ai-interview-action-modal"
      header="AI interview configuration"
    >
      <div className="edit-ai-interview-action-modal__content">
        <SelectFormField<LivekitAgentName>
          id={`ai-interview-action-${aiInterviewAction.id}-livekit-agent`}
          label="LiveKit agent"
          value={selectedOption}
          options={LIVEKIT_AGENT_OPTIONS}
          onChange={handleAgentChange}
          hasNullValue={false}
          menuPosition="fixed"
        />
        <Switch
          id={`ai-interview-action-${aiInterviewAction.id}-avatar-enabled`}
          label="Avatar enabled"
          value={isAvatarEnabled}
          onToggle={setIsAvatarEnabled}
          dataTestId="edit-ai-interview-action-modal__avatar-toggle"
        />
        <Switch
          id={`ai-interview-action-${aiInterviewAction.id}-timing-agent-enabled`}
          label="Timing agent enabled"
          value={isTimingAgentEnabled}
          onToggle={setIsTimingAgentEnabled}
          dataTestId="edit-ai-interview-action-modal__timing-agent-toggle"
        />
      </div>
      <div className="edit-ai-interview-action-modal__actions">
        <Button onClick={onClose} size="medium" variant="secondary">
          Cancel
        </Button>
        <Button
          size="medium"
          disabled={submitting}
          onClick={() => handleSave()}
          data-testid="edit-ai-interview-action-modal__save-button"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default EditAiInterviewActionModal;
